// React and Next.js core imports
import React from "react";

// Types
interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

// Global Error Boundary Component
const GlobalErrorBoundary: React.FC<Props> = ({ children }) => {
  const [state, setState] = React.useState<State>({
    hasError: false,
    error: null,
  });

  React.useEffect(() => {
    // Initialize error tracking
    const handleError = (error: ErrorEvent) => {
      setState({ hasError: true, error: error.error });
      console.error("Global error caught:", error.error);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      setState({ hasError: true, error: event.reason });
      console.error("Unhandled promise rejection:", event.reason);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection
      );
    };
  }, []);

  if (state.hasError) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4 bg-background">
        <div className="max-w-md w-full space-y-8 p-8 bg-card rounded-lg shadow-lg">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-destructive mb-4">
              Oops! Something went wrong
            </h2>
            <p className="text-muted-foreground mb-6">
              We apologize for the inconvenience. Our team has been notified and
              is working to fix the issue.
            </p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  setState({ hasError: false, error: null });
                  window.location.reload();
                }}
                className="w-full px-4 py-2 bg-info text-info-foreground rounded hover:bg-info/90 transition-colors"
              >
                Try again
              </button>
              <button
                onClick={() => (window.location.href = "/")}
                className="w-full px-4 py-2 bg-muted text-foreground rounded hover:bg-accent transition-colors"
              >
                Go to Homepage
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default GlobalErrorBoundary;
